import { useState } from 'react';

import { useRouter } from 'expo-router';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { calculateCartSubTotal } from 'features/cart/utils';
import { EnvItem } from 'features/diagnostics/components/env-item';
import { usePriceOrderV2Input } from 'features/order/hooks/use-price-order-v2-input';
import { Actionsheet } from 'native-base';
import { ScrollView, Text, View } from 'react-native';

import { Footer, InlineModal } from 'components/ui';
import { IMenuSelectionInput } from 'generated/graphql-gateway';
import { routes } from 'navigation/routing';

import { CartDebugActionButton } from './cart-debug-action-button';
import { CartDebugManageDonationsModal } from './cart-debug-manage-donations-modal';
import { CartDebugManageUtensilsModal } from './cart-debug-manage-utensils-modal';
import { CartDebugSubtotalModal } from './cart-debug-subtotal-modal';

const deriveMenuSelections = (
  menuSelections: readonly IMenuSelectionInput[]
): IMenuSelectionInput[] => {
  return menuSelections.map(selection => ({
    ...selection,
    menuSelections: deriveMenuSelections([...(selection.menuSelections ?? [])]),
  }));
};

const EnvItemModal = ({
  isOpen,
  onClose,
  envKey,
  value,
}: {
  isOpen: boolean;
  onClose: () => void;
  envKey: string;
  value: string;
}) => {
  if (isOpen) {
    // eslint-disable-next-line no-console
    console.log({ [envKey]: JSON.parse(value) });
  }

  return (
    <InlineModal
      isOpen={isOpen}
      onClose={onClose}
      header={{ title: `Debug ${envKey}`, variant: 'light' }}
    >
      <ScrollView>
        <EnvItem envKey={envKey} value={value} />
      </ScrollView>
    </InlineModal>
  );
};

export const CartDebugFooter = () => {
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isUtensilsOpen, setIsUtensilsOpen] = useState(false);
  const [isDonationsOpen, setIsDonationsOpen] = useState(false);
  const [isMinimalCartDebugOpen, setIsMinimalCartDebugOpen] = useState(false);
  const [isFullCartDebugOpen, setIsFullCartDebugOpen] = useState(false);
  const [isPriceOrderInputDebugOpen, setIsPriceOrderInputDebugOpen] = useState(false);
  const [isSubTotalDebugOpen, setIsSubTotalDebugOpen] = useState(false);
  const { emptyMinimalCart } = useMinimalCart();
  const { cartEntries } = useMinimalCart();
  const { navigate } = useRouter();

  const menuSelections = cartEntries.map(
    entry =>
      ({
        id: entry.id,
        quantity: entry.quantity,
        price: entry.price,
        menuSelections: deriveMenuSelections(entry.menuSelections ?? []),
      } as IMenuSelectionInput)
  );

  const subTotal = calculateCartSubTotal(cartEntries);
  const priceOrderInput = usePriceOrderV2Input(menuSelections);

  const handleViewActions = (type: 'viewMinimalCart' | 'viewPriceOrderInput' | 'viewFullCart') => {
    setIsViewOpen(false);

    setTimeout(() => {
      if (type === 'viewMinimalCart') {
        setIsMinimalCartDebugOpen(true);
      }

      if (type === 'viewPriceOrderInput') {
        setIsPriceOrderInputDebugOpen(true);
      }
      if (type === 'viewFullCart') {
        setIsFullCartDebugOpen(true);
      }
    }, 300);
  };

  return (
    <Footer style={{ paddingVertical: 0, paddingHorizontal: 0 }}>
      <View style={{ flexDirection: 'column', gap: 6 }}>
        <Text style={{ fontSize: 16, fontWeight: 'bold', alignSelf: 'center' }}>
          SubTotal: {subTotal}
        </Text>
        <View style={{ flexDirection: 'row', gap: 6 }}>
          <CartDebugActionButton onPress={() => setIsSubTotalDebugOpen(true)}>
            Debug subTotal
          </CartDebugActionButton>
          <CartDebugActionButton onPress={() => emptyMinimalCart()}>
            Clear Cart
          </CartDebugActionButton>
          <CartDebugActionButton onPress={() => setIsViewOpen(true)}>View...</CartDebugActionButton>
          <CartDebugActionButton onPress={() => setIsAddOpen(true)}>Add...</CartDebugActionButton>
        </View>
      </View>

      <Actionsheet isOpen={isViewOpen} onClose={() => setIsViewOpen(false)}>
        <Actionsheet.Content>
          <Actionsheet.Item onPress={() => handleViewActions('viewMinimalCart')}>
            View Minimal Cart
          </Actionsheet.Item>
          <Actionsheet.Item onPress={() => handleViewActions('viewFullCart')}>
            View Full Cart
          </Actionsheet.Item>
          <Actionsheet.Item onPress={() => handleViewActions('viewPriceOrderInput')}>
            View Price Order Input
          </Actionsheet.Item>
        </Actionsheet.Content>
      </Actionsheet>

      <Actionsheet isOpen={isAddOpen} onClose={() => setIsAddOpen(false)}>
        <Actionsheet.Content>
          <Actionsheet.Item
            onPress={() => {
              navigate(routes.menu);
              setIsAddOpen(false);
            }}
          >
            Add Items
          </Actionsheet.Item>
          <Actionsheet.Item
            onPress={() => {
              navigate(routes.offers);
              setIsAddOpen(false);
            }}
          >
            Add Offers
          </Actionsheet.Item>
          <Actionsheet.Item
            onPress={() => {
              navigate(routes.rewards);
              setIsAddOpen(false);
            }}
          >
            Add Rewards
          </Actionsheet.Item>
          <Actionsheet.Item
            onPress={() => {
              setIsAddOpen(false);
              setIsUtensilsOpen(true);
            }}
          >
            Add Utensils
          </Actionsheet.Item>
          <Actionsheet.Item
            onPress={() => {
              setIsAddOpen(false);
              setIsDonationsOpen(true);
            }}
          >
            Add Donation
          </Actionsheet.Item>
        </Actionsheet.Content>
      </Actionsheet>

      <EnvItemModal
        envKey={'Minimal cart'}
        value={JSON.stringify({ cart: menuSelections })}
        isOpen={isMinimalCartDebugOpen}
        onClose={() => setIsMinimalCartDebugOpen(false)}
      />
      <EnvItemModal
        envKey={'Full cart'}
        value={JSON.stringify({ cart: cartEntries })}
        isOpen={isFullCartDebugOpen}
        onClose={() => setIsFullCartDebugOpen(false)}
      />
      <EnvItemModal
        envKey={'Price Order Input'}
        value={JSON.stringify({ input: priceOrderInput })}
        isOpen={isPriceOrderInputDebugOpen}
        onClose={() => setIsPriceOrderInputDebugOpen(false)}
      />
      <CartDebugSubtotalModal
        cartEntries={cartEntries}
        isOpen={isSubTotalDebugOpen}
        onClose={() => setIsSubTotalDebugOpen(false)}
      />
      <CartDebugManageUtensilsModal
        isOpen={isUtensilsOpen}
        onClose={() => setIsUtensilsOpen(false)}
      />
      <CartDebugManageDonationsModal
        isOpen={isDonationsOpen}
        onClose={() => setIsDonationsOpen(false)}
      />
    </Footer>
  );
};
